import React, { useEffect, useState } from "react";
import { FaCheck, FaRegCopy } from "react-icons/fa";
import { NavLink, Link } from "react-router-dom";
import { CardInfoTableGroupSelected } from "./CardInfoTableGroupSelected";
import { FeedbackState } from "./FeedbackState";
import { FixtureBets } from "../pages/FixtureBets";
import GroupLeagueBets from "../containers/LeagueBets/GroupLeagueBets";

const MOBILE_TABS = {
	PREDICTIONS: "predictions",
	STANDINGS: "standings",
	CHAMPIONSHIP: "championship",
};

function GroupDashboardView({
	currentUniqueIdentifierSelected,
	selectedGroup,
	infoLeagueTeamSelected,
	loadingLeagueTeamSelected,
	leagueTeamSelectedError,
	loadingLeaveGroup,
	onRetryLeagueBets,
	onLeaveGroup,
	onCopyAccessCode,
}) {
	const [mobileActiveTab, setMobileActiveTab] = useState(MOBILE_TABS.PREDICTIONS);
	const [copiedAccessCode, setCopiedAccessCode] = useState(false);

	const groupName = selectedGroup?.name || "";
	const isGroupAdmin = selectedGroup?.isGroupAdmin || false;
	const accessCode = selectedGroup?.accessCode || "";
	const allowLeagueBets =
		selectedGroup?.groupLeagues?.some((league) => league.allowLeagueBets) ||
		false;
	const hasChampionshipBets =
		allowLeagueBets && infoLeagueTeamSelected.Result?.length > 0;

	useEffect(() => {
		setMobileActiveTab(MOBILE_TABS.PREDICTIONS);
	}, [currentUniqueIdentifierSelected]);

	useEffect(() => {
		if (mobileActiveTab === MOBILE_TABS.CHAMPIONSHIP && !hasChampionshipBets) {
			setMobileActiveTab(MOBILE_TABS.PREDICTIONS);
		}
	}, [hasChampionshipBets, mobileActiveTab]);

	useEffect(() => {
		if (!copiedAccessCode) {
			return undefined;
		}

		const timeoutId = window.setTimeout(() => {
			setCopiedAccessCode(false);
		}, 2000);

		return () => window.clearTimeout(timeoutId);
	}, [copiedAccessCode]);

	const handleCopyAccessCode = async () => {
		const didCopy = await onCopyAccessCode(accessCode);
		if (didCopy) {
			setCopiedAccessCode(true);
		}
	};

	const renderLeagueBetsCard = () => (
		<section className='group-dashboard-card card border-0'>
			<div className='card-header card-header-color text-center'>
				<strong>Pronosticos de campeonato</strong>
			</div>
			<div className='card-body'>
				{loadingLeagueTeamSelected ? (
					<FeedbackState
						variant='loading'
						title='Cargando pronosticos de campeonato'
						description='Estamos preparando las opciones disponibles para este grupo.'
						compact
					/>
				) : leagueTeamSelectedError ? (
					<FeedbackState
						variant='error'
						title='No pudimos cargar este extra'
						description={leagueTeamSelectedError}
						actionLabel='Reintentar'
						onAction={onRetryLeagueBets}
						compact
					/>
				) : !infoLeagueTeamSelected.Result?.length ? (
					<FeedbackState
						variant='empty'
						title='No hay picks de campeonato disponibles'
						description='Cuando este grupo habilite ese extra para el torneo, lo veras aqui.'
						compact
					/>
				) : (
					<div className='card-title text-center mb-3'>
						<GroupLeagueBets
							currentUniqueIdentifierSelected={currentUniqueIdentifierSelected}
							leagueBets={infoLeagueTeamSelected.Result}
						/>
					</div>
				)}
			</div>
		</section>
	);

	const renderFixtureBetsCard = () => (
		<section className='group-dashboard-card card border-0'>
			<div className='card-header card-header-color text-center'>
				<strong>Partidos por pronosticar</strong>
			</div>

			<div className='card-body'>
				<div className='card-title text-center mb-3'>
					<FixtureBets />
				</div>
			</div>
		</section>
	);

	const renderStandingsCard = () => (
		<section className='group-dashboard-card card border-0'>
			<div className='card-header card-header-color text-center'>
				<strong>Tabla de posiciones</strong>
			</div>
			<div className='card-body standings-card-body'>
				{isGroupAdmin && (
					<div className='group-side-note group-side-note-invite mb-4'>
						<div>
							<div className='group-side-note-label'>Codigo de invitacion</div>
							<div className='group-side-note-value'>{accessCode}</div>
						</div>
						<button
							type='button'
							className='btn btn-light group-copy-code-button'
							onClick={handleCopyAccessCode}
						>
							{copiedAccessCode ? (
								<FaCheck aria-hidden='true' />
							) : (
								<FaRegCopy aria-hidden='true' />
							)}
							<span>{copiedAccessCode ? "Copiado" : "Copiar"}</span>
						</button>
					</div>
				)}
				<CardInfoTableGroupSelected
					currentUniqueIdentifierSelected={currentUniqueIdentifierSelected}
				/>
			</div>
		</section>
	);

	const renderMobileTabButton = (tabId, label) => (
		<button
			key={tabId}
			type='button'
			className={`group-mobile-tab-button ${
				mobileActiveTab === tabId ? "is-active" : ""
			}`}
			onClick={() => setMobileActiveTab(tabId)}
			aria-pressed={mobileActiveTab === tabId}
		>
			{label}
		</button>
	);

	const renderMobileTabPanel = () => {
		if (mobileActiveTab === MOBILE_TABS.STANDINGS) {
			return renderStandingsCard();
		}

		if (mobileActiveTab === MOBILE_TABS.CHAMPIONSHIP && hasChampionshipBets) {
			return renderLeagueBetsCard();
		}

		return renderFixtureBetsCard();
	};

	return (
		<section className='group-dashboard-page mt-4'>
			<div className='group-toolbar card border-0'>
				<div className='card-body'>
					<div className='group-toolbar-layout'>
						<div className='group-toolbar-copy'>
							<h2 className='group-toolbar-title mb-2'>{groupName}</h2>
							<p className='group-toolbar-subtitle mb-0'>
								Entra directo a los pronosticos y deja las acciones de gestion
								en un segundo plano.
							</p>
						</div>

						<div className='group-toolbar-actions'>
							<div className='group-toolbar-actions-secondary'>
								{isGroupAdmin && (
									<Link
										to={`/edit-group/${currentUniqueIdentifierSelected}`}
										className='btn btn-outline-primary buttons-page-groups'
									>
										Editar grupo
									</Link>
								)}
								<Link
									to='/groups/join'
									className='btn btn-outline-secondary buttons-page-groups'
								>
									Unirme a otro grupo
								</Link>
								<NavLink
									to='/create-new-group'
									className='btn btn-outline-secondary buttons-page-groups'
								>
									Crear grupo
								</NavLink>
							</div>

							<button
								type='button'
								className='btn btn-danger buttons-page-groups group-leave-button'
								onClick={onLeaveGroup}
								disabled={loadingLeaveGroup}
							>
								{loadingLeaveGroup ? "Saliendo..." : "Abandonar grupo"}
							</button>
						</div>
					</div>
				</div>
			</div>

			<div className='group-dashboard-desktop'>
				<div className='row g-4 mt-1 group-dashboard-layout'>
					<div className='col-12 col-lg-8'>{renderFixtureBetsCard()}</div>
					<div className='col-12 col-lg-4'>
						<div className='group-dashboard-sidebar'>
							{allowLeagueBets && renderLeagueBetsCard()}
							{renderStandingsCard()}
						</div>
					</div>
				</div>
			</div>

			<div className='group-dashboard-mobile'>
				<div className='group-mobile-tabs mt-4'>
					<div
						className='group-mobile-tabs-nav'
						role='tablist'
						aria-label='Secciones del grupo'
					>
						{renderMobileTabButton(MOBILE_TABS.PREDICTIONS, "Pronosticos")}
						{renderMobileTabButton(MOBILE_TABS.STANDINGS, "Posiciones")}
						{allowLeagueBets &&
							renderMobileTabButton(MOBILE_TABS.CHAMPIONSHIP, "Campeonato")}
					</div>
					<div className='group-mobile-tab-panel' role='tabpanel'>
						{renderMobileTabPanel()}
					</div>
				</div>
			</div>
		</section>
	);
}

export { GroupDashboardView };
